/**
 * CivicStage — investitures, election hustings and speeches: a lectern on a
 * low dais where the host stands, two standards carrying the event title, a
 * floating title plate, and torches (speeches) that flicker. The crowd faces
 * the lectern (EnvironmentModule's `facing` map, filled by LivingEventsModule).
 * THE TORCHES (fire realism R12, 2026-09-25) burn through the fire system
 * (kind 'torch': a small flame on the tier's rung, a light lent by the shared
 * broker, the filtered flicker) on a post with a pitch-soaked head; the glow
 * sprite with a sine flicker is only the fallback when no fire system is up.
 */
import * as THREE from 'three/webgpu';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import { glowTexture, textTexture, spriteOf } from '../fx_textures.js';
import { type Stage, type StageCtx, disposeGroup, makeBanner, waveCloth, propMaterial } from './StageBase.js';
import { ceremonyProp } from '../ceremony_props_loader.js';
import { addFire, removeFire } from '../../../lib/vfx/fire_system.js';

let torchSeq = 0;

export class CivicStage implements Stage {
  readonly group = new THREE.Group();
  private banners: THREE.Group[] = [];
  private torches: THREE.Sprite[] = [];
  private title: THREE.Sprite;
  private generated = 0;
  /** The torches' keys in the fire system. */
  private torchFires: string[] = [];

  constructor(private readonly ctx: StageCtx) {
    const { x, z, y, def } = ctx;
    this.group.position.set(x, y, z);
    ctx.root.add(this.group);
    const props = new Set(def.props);
    // The host stands at (+1.2, 0) facing −x toward the fan of listeners; the
    // lectern sits in front of them.
    const wood = propMaterial(0x6b4a2e, 0.85);
    if (props.has('podium')) {
      const dais = new THREE.Mesh(new THREE.BoxGeometry(2.6, 0.18, 2.2), propMaterial(0x8e8272, 0.95));
      dais.position.set(1.4, 0.09, 0);
      this.group.add(dais);
      // The generated lectern (Hugging Face wave) on the dais, its reading
      // face toward the listeners (−x); the box lectern stands in until it lands.
      const genLectern = ceremonyProp('lectern', -Math.PI / 2);
      if (genLectern) { genLectern.position.set(0.35, 0.18, 0); this.group.add(genLectern); this.generated++; }
      else {
        const lectern = new THREE.Mesh(new THREE.BoxGeometry(0.55, 1.1, 0.4), wood);
        lectern.position.set(0.35, 0.55, 0);
        const top = new THREE.Mesh(new THREE.BoxGeometry(0.6, 0.05, 0.45), wood);
        top.position.set(0.33, 1.12, 0);
        top.rotation.z = 0.25;
        this.group.add(lectern, top);
      }
    }
    if (props.has('banners')) {
      for (const s of [-1, 1]) {
        const b = makeBanner(ctx.title, s < 0 ? 0x8a2f2f : 0x2f4f8a);
        b.position.set(2.6, ctx.groundAt(x + 2.6, z + s * 2.4) - y, s * 2.4);
        b.rotation.y = Math.PI / 2;
        this.group.add(b);
        this.banners.push(b);
      }
    }
    // CIVIC WAVE kind dressing: the urn on voting day, the red banner over a
    // declaration, the accord table + dove-white standard at a treaty or
    // congress. Family stays 'civic' — the kinds differ by furniture.
    if (def.kind === 'ballot') {
      const genUrn = ceremonyProp('ballot_urn', -Math.PI / 2);
      if (genUrn) { genUrn.position.set(0.3, 0.18, 0.9); this.group.add(genUrn); this.generated++; }
      else {
        const urn = new THREE.Mesh(new THREE.CylinderGeometry(0.28, 0.34, 0.7, 10), propMaterial(0x3a4a5a, 0.6));
        urn.position.set(0.3, 0.53, 0.9);
        const slotTop = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.06, 0.5), wood);
        slotTop.position.set(0.3, 0.91, 0.9);
        this.group.add(urn, slotTop);
      }
    }
    if (props.has('war_banner')) {
      const wb = makeBanner('WAR', 0x8a1f1f);
      wb.position.set(-0.6, 0, 0);
      wb.scale.setScalar(1.4);
      this.group.add(wb);
      this.banners.push(wb);
    }
    if (def.kind === 'treaty' || def.kind === 'congress') {
      const table = new THREE.Mesh(new THREE.BoxGeometry(1.8, 0.12, 0.9), wood);
      table.position.set(0.4, 0.72, -0.9);
      const legs = new THREE.Mesh(new THREE.BoxGeometry(1.6, 0.66, 0.7), propMaterial(0x4a3a2a, 0.9));
      legs.position.set(0.4, 0.33, -0.9);
      const white = makeBanner(def.kind === 'congress' ? 'CONGRESS' : 'ACCORD', 0xd8d2c4);
      white.position.set(-0.4, 0, -2.0);
      this.group.add(table, legs, white);
      this.banners.push(white);
    }
    if (props.has('torches')) {
      for (const s of [-1, 1]) {
        const post = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.06, 2.2, 6), wood);
        post.position.set(1.2, 1.1, s * 1.6);
        this.group.add(post);
        // The head: a pitch-wrapped bundle the flame burns on.
        const head = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.06, 0.24, 7), propMaterial(0x2a2119, 1));
        head.position.set(1.2, 2.3, s * 1.6);
        this.group.add(head);
        const key = `civic-torch:${Math.round(x)}:${Math.round(z)}:${++torchSeq}`;
        const tx = x + 1.2, tz = z + s * 1.6;
        if (addFire(key, { x: tx, y: y + 2.4, z: tz, kind: 'torch', groundY: y })) { this.torchFires.push(key); continue; }
        const f = spriteOf(glowTexture(), 0xffa24a, 0.8);
        f.position.set(1.2, 2.4, s * 1.6);
        this.group.add(f);
        this.torches.push(f);
      }
    }
    const tex = textTexture([
      { text: `${def.glyph} ${def.label}`, font: '700 34px system-ui' },
      { text: ctx.title.slice(0, 40), font: '26px system-ui', color: '#d9d2ff' },
    ]);
    this.title = new THREE.Sprite(markMrtOverlay(new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false })));
    (this.title.material as THREE.SpriteMaterial & { ownsMap?: boolean }).ownsMap = true;
    this.title.scale.set(5.2, 1.6, 1);
    this.title.position.set(1.2, 3.6, 0);
    this.group.add(this.title);
  }

  update(t: number): void {
    this.banners.forEach((b, i) => waveCloth(b, t, i * 2));
    this.torches.forEach((f, i) => {
      f.scale.setScalar(0.8 * (1 + Math.sin(t * (9 + i) + i) * 0.18 + Math.sin(t * 13 + i * 2) * 0.08)); // flicker by scale
    });
    this.title.position.y = 3.6 + Math.sin(t * 0.8) * 0.05;
    const left = this.ctx.remainingS();
    if (left < 20) (this.title.material as THREE.SpriteMaterial).opacity = Math.max(0.15, left / 20);
  }

  describe(): Record<string, number | string | boolean> {
    return { family: 'civic', banners: this.banners.length, torches: this.torches.length + this.torchFires.length, lectern: this.ctx.def.props.includes('podium'), lights: 0, fires: this.torchFires.length, generated: this.generated };
  }

  dispose(): void {
    for (const k of this.torchFires) removeFire(k);
    this.torchFires.length = 0;
    disposeGroup(this.group);
  }
}
